import vtkMatrixBuilder from 'vtk.js/Sources/Common/Core/MatrixBuilder';
import vtkPolyData from 'vtk.js/Sources/Common/DataModel/PolyData';
import { mat4 } from 'gl-matrix';

const FLOAT32_SZ = 4;
const INT32_SZ = 4;

// point step size
const PT_STEP_SZ = 3 * FLOAT32_SZ;
// texture step size
const TXT_STEP_SZ = 2 * FLOAT32_SZ;
// color step size,uchar format
const CLR_STEP_SZ = 3;

class PLYHeader {
  comments: string[];
  elements: PLYElement[];
}

class ElementProp {
  type: string;
  name: string;
}

class PLYElement {
  name: string;
  count: number;
  props: ElementProp[];
}

class PLYBuffer {
  indices: number[];
  vertices: number[];
  uvs: number[];
  colors: number[];
}

class CellIndex {
  data: number[] = [];
}

class CellIndexGroup {
  indexDataCount = 0;
  indexArr: CellIndex[] = [];
}

export default class PLYWriter {
  private header: PLYHeader;
  private buffer: PLYBuffer;
  private polyData: vtkPolyData;
  private transformMat: mat4;
  private outBuf: Buffer;
  private textureFile: string;
  private hasTexture: boolean;
  private hasColor: boolean;

  constructor() {
    this.header = {
      comments: ['Generated by CS WebViewer'],
      elements: [],
    };
    this.buffer = {
      indices: [],
      vertices: [],
      //   normals: [],
      uvs: [],
      //   faceVertexUvs: [],
      colors: [],
    };
  }

  writeData(data: vtkPolyData, transform: mat4, textureFile?: string) {
    if (!data) {
      return;
    }
    this.polyData = data;
    this.transformMat = transform;
    this.textureFile = textureFile;
    this.parsePolyData();
    this.writeHeader();
    this.writeBuffer();
    return this.outBuf;
  }

  private parsePolyData() {
    const ptsNum = this.polyData.getNumberOfPoints();
    if (ptsNum <= 0) {
      return;
    }
    const eleVertex: PLYElement = {
      name: 'vertex',
      count: ptsNum,
      props: [],
    };
    eleVertex.props.push({ type: 'float', name: 'x' });
    eleVertex.props.push({ type: 'float', name: 'y' });
    eleVertex.props.push({ type: 'float', name: 'z' });
    this.parseColors(eleVertex);
    this.header.elements.push(eleVertex);
    for (let i = 0; i < ptsNum; ++i) {
      const pt = this.polyData.getPoints().getPoint(i);
      for (let j = 0; j < 3; ++j) {
        this.buffer.vertices.push(pt[j]);
      }
    }
    // transform apply
    if (this.transformMat) {
      const transform = vtkMatrixBuilder.buildFromDegree();
      transform.setMatrix(this.transformMat).apply(this.buffer.vertices);
    }
    this.parseFace();
  }

  private writeHeader() {
    // begin
    let hstr = 'ply\n';
    // format
    hstr += 'format binary_little_endian 1.0\n';
    // comments
    this.header.comments.forEach((item) => {
      hstr = hstr + 'comment ' + item + '\n';
    });
    // elements
    for (let i = 0; i < this.header.elements.length; ++i) {
      let ele = this.header.elements[i];
      hstr = hstr + 'element ' + ele.name + ' ' + ele.count + '\n';
      ele.props.forEach((item) => {
        hstr = hstr + 'property ' + item.type + ' ' + item.name + '\n';
      });
    }
    // end
    hstr += 'end_header\n';
    this.outBuf = Buffer.from(hstr);
  }

  private writeBuffer() {
    let tmpBufSz = this.buffer.vertices.length * FLOAT32_SZ;
    if (this.hasTexture) {
      tmpBufSz += this.buffer.uvs.length * FLOAT32_SZ;
    } else if (this.hasColor) {
      tmpBufSz += this.buffer.colors.length;
    }
    const tmp = Buffer.alloc(tmpBufSz);
    const ptsNum = this.buffer.vertices.length / 3;
    for (let i = 0; i < ptsNum; ++i) {
      const indexLeft = i * 3;
      let offsetRight = i * PT_STEP_SZ;
      if (this.hasTexture) {
        offsetRight = i * (PT_STEP_SZ + TXT_STEP_SZ);
      } else if (this.hasColor) {
        offsetRight = i * (PT_STEP_SZ + CLR_STEP_SZ);
      }
      for (let j = 0; j < 3; ++j) {
        const val = this.buffer.vertices[indexLeft + j];
        tmp.writeFloatLE(val, offsetRight + j * FLOAT32_SZ);
      }
    }
    if (this.hasTexture) {
      this.writeTextureCoords(tmp, ptsNum);
    } else if (this.hasColor) {
      this.writeColors(tmp, ptsNum);
    }
    const len = tmp.length + this.outBuf.length;
    this.outBuf = Buffer.concat([this.outBuf, tmp], len);
    this.writeFace();
  }

  private parseFace() {
    const polys = this.polyData.getPolys();
    const faceData = polys.getData();
    const faceCount = polys.getNumberOfCells();
    if (!faceData || faceData.length <= 0 || faceCount <= 0) {
      return;
    }
    this.buffer.indices = Array.from(faceData);
    // update header info
    const eleFace = {
      name: 'face',
      count: faceCount,
      props: [],
    };
    eleFace.props.push({ type: 'list uchar int vertex_indices', name: '' });
    this.header.elements.push(eleFace);
  }

  private writeFace() {
    const group = this.parseCellIndexGroup(this.buffer.indices);
    const bufSz = group.indexArr.length + group.indexDataCount * INT32_SZ;
    const buf = Buffer.alloc(bufSz);
    let offset = 0;
    group.indexArr.forEach((index) => {
      buf.writeUInt8(index.data.length, offset);
      offset += 1;
      index.data.forEach((e) => {
        buf.writeInt32LE(e, offset);
        offset += INT32_SZ;
      });
    });
    const len = buf.length + this.outBuf.length;
    this.outBuf = Buffer.concat([this.outBuf, buf], len);
  }

  private parseColors(eleVertex: PLYElement) {
    const pd = this.polyData.getPointData();
    this.buffer.uvs = Array.from(pd?.getTCoords()?.getData());
    if (this.buffer.uvs?.length > 0 && this.textureFile?.length > 0) {
      eleVertex.props.push({ type: 'float', name: 'texture_u' });
      eleVertex.props.push({ type: 'float', name: 'texture_v' });
      this.header.comments.push('TextureFile ' + this.textureFile);
      this.hasTexture = true;
    }
    if (this.hasTexture) {
      return;
    }
    // if no texture,try parse colors
    this.buffer.colors = Array.from(pd?.getScalars()?.getData());
    if (this.buffer.colors?.length > 0) {
      eleVertex.props.push({ type: 'uchar', name: 'red' });
      eleVertex.props.push({ type: 'uchar', name: 'green' });
      eleVertex.props.push({ type: 'uchar', name: 'blue' });
      this.hasColor = true;
    }
  }

  private writeColors(buf: Buffer, ptsNum: number) {
    for (let i = 0; i < ptsNum; ++i) {
      const indexLeft = i * 3;
      const offsetRight = i * (PT_STEP_SZ + CLR_STEP_SZ);
      for (let j = 0; j < 3; ++j) {
        const val = this.buffer.colors[indexLeft + j];
        buf.writeUInt8(val, offsetRight + PT_STEP_SZ + j);
      }
    }
  }

  private writeTextureCoords(buf: Buffer, ptsNum: number) {
    for (let i = 0; i < ptsNum; ++i) {
      const indexLeft = i * 2;
      const offsetRight = i * (PT_STEP_SZ + TXT_STEP_SZ);
      for (let j = 0; j < 2; ++j) {
        const val = this.buffer.uvs[indexLeft + j];
        buf.writeFloatLE(val, offsetRight + PT_STEP_SZ + j * FLOAT32_SZ);
      }
    }
  }

  private parseCellIndexGroup(indices: number[]) {
    const ret = new CellIndexGroup();
    for (let i = 0; i < indices?.length; ++i) {
      const indexCount = indices[i];
      if (indexCount > 0 && i + indexCount < indices.length) {
        const index = new CellIndex();
        for (let j = 1; j <= indexCount; ++j) {
          index.data.push(indices[i + j]);
        }
        ret.indexArr.push(index);
        ret.indexDataCount += indexCount;
        i += indexCount;
      }
    }
    return ret;
  }
}
